/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { useEffect, useMemo, useState, type ChangeEventHandler } from 'react'
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import { StyledInput } from 'components/widgets'
import { PRIMARY_COLOR } from 'lib/constants'
import type { Organization, User } from 'lib/types'
import { updateOrganization } from 'models/api/orgs'
import { createLicense, deleteLicense } from 'models/api/licenses'
import { getUsersByOrgId } from 'models/api/users'
import { useGlobal } from 'contexts/GlobalProvider'
import { isSuperAdmin } from 'lib/utils/auth'
import { essentialOnlyButtonsFlag } from 'lib/utils/featureFlags'
import { EssentialOnlyButtonsSection } from 'components/partials/EssentialOnlyButtonsSection'

/* ── layout ─────────────────────────────────────────────── */

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 840,
})

// Assigned voice users and essential-only buttons side by side, stacking when narrow.
const TablesRow = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 32,
  marginTop: 20,
  '& > *': { flex: '0 1 400px', minWidth: 0 },
})

const TwoCol = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 32,
  marginTop: 8,
})

const Col = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 140,
})

const ColLabel = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '1.0rem',
  color: '#242424',
  marginBottom: 8,
})

/* box that matches the screenshot — shared by input and read-only value */
const ValueBox = styled.div({
  display: 'flex',
  alignItems: 'center',
  background: '#f5f5f5',
  borderRadius: 6,
  padding: '0 10px',
  fontFamily: 'Montserrat',
  fontSize: '12px',
  fontWeight: 600,
  color: '#242424',
  width: 160,
  height: 26,
})

const NumberInput = styled(StyledInput)({
  width: 160,
  marginTop: 8,
  height: 26,
  minHeight: 26,
  '& .MuiInputBase-root': { height: 26, minHeight: 26, alignItems: 'center' },
  '& .MuiInputBase-input': {
    paddingTop: 0,
    paddingBottom: 0,
    height: 26,
    lineHeight: '26px',
    fontSize: '12px',
    boxSizing: 'border-box',
  },
  '& .MuiInput-underline:before, & .MuiInput-underline:after': { bottom: 0 },
})

/* pill for "X / Y" — wider than the default NumberBubble */
const LicensePill = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e6e6e6',
  borderRadius: '11.5px',
  padding: '0 12px',
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '1.2rem',
  color: '#242424',
  height: 23,
  minWidth: 56,
})

const InfoTrigger = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 0,
  cursor: 'pointer',
  color: PRIMARY_COLOR,
  opacity: 0.8,
})

/* ── edit controls ───────────────────────────────────────── */

const ActionButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '6px 16px',
  border: `2px solid ${PRIMARY_COLOR}`,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat',
  fontSize: '0.95rem',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.08rem',
  marginRight: 6,
  borderRadius: 4,
  '&.Mui-disabled': { opacity: 0.5, boxShadow: 'none' },
})

const ActionsRow = styled.div({
  marginTop: 10,
})

/* ── table ───────────────────────────────────────────────── */

const TableSection = styled.div({})

const TableSectionHeader = styled.div({
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '1.0rem',
  color: '#242424',
  marginBottom: 6,
})

// Fits the Add button (MUI's 64px min width, padding and letter spacing).
const ACTION_COL_WIDTH = 88

const cellStyle: React.CSSProperties = {
  fontFamily: 'Montserrat',
  fontSize: 12,
  padding: '5px 12px',
}

/* ── component ───────────────────────────────────────────── */

type VoiceSettingsProps = {
  org: Organization | null
  setSettingsOrg?: (org: Organization | null) => void
}

const VoiceSettings = ({ org, setSettingsOrg }: VoiceSettingsProps) => {
  const { licenses, licensesLoading, licensesError, reloadLicenses, selectedOrgId, selectedOrg, setSelectedOrg } =
    useGlobal()
  const superAdmin = isSuperAdmin()

  const [orgUsers, setOrgUsers] = useState<User[]>([])

  const originalMax = useMemo<number | ''>(() => {
    if (!org) return ''
    return typeof (org as any).zelloUserMax === 'number' ? (org as any).zelloUserMax : ''
  }, [org?.id, (org as any)?.zelloUserMax]) // eslint-disable-line react-hooks/exhaustive-deps

  const [maxValue, setMaxValue] = useState<number | ''>(originalMax)
  const [maxSaving, setMaxSaving] = useState(false)
  const [maxError, setMaxError] = useState('')

  const [addUserId, setAddUserId] = useState('')
  const [addError, setAddError] = useState('')
  const [addLoading, setAddLoading] = useState(false)

  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    setMaxValue(originalMax)
    setMaxError('')
  }, [org?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedOrgId) return
    getUsersByOrgId(selectedOrgId).then(setOrgUsers).catch(() => {})
  }, [selectedOrgId])

  const assignedRows = useMemo(() => {
    return (licenses || [])
      .filter(l => l.type === 'standalone_voice')
      .map(l => {
        // The v5 index computes `subjectName` server-side — a user's email, or
        // an alert source's name falling back to its id — which is the only way
        // to name a licensee who belongs to a parent org and so never appears in
        // `orgUsers`. The local lookup behind it only covers an older API that
        // doesn't send the field yet.
        let label: string
        if (l.subjectName) {
          label = l.subjectName
        } else if (l.userId) {
          const user = orgUsers.find(u => u.id === String(l.userId))
          label = user?.attributes?.email ?? String(l.userId)
        } else {
          label = l.alertSourceId ?? '—'
        }
        return { license: l, label }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [licenses, orgUsers])

  const assignedUserIds = useMemo(() => new Set(assignedRows.map(r => String(r.license.userId))), [assignedRows])

  const unassignedUsers = useMemo(
    () => orgUsers.filter(u => !assignedUserIds.has(u.id)).sort((a, b) => (a.attributes?.email ?? '').localeCompare(b.attributes?.email ?? '')),
    [orgUsers, assignedUserIds],
  )

  const isDirty = maxValue !== originalMax

  const handleMaxChange: ChangeEventHandler<HTMLInputElement> = e => {
    const v = e.target.value
    if (v === '') { setMaxValue(''); setMaxError(''); return }
    const n = Number(v)
    if (!Number.isNaN(n) && n >= 0) {
      setMaxValue(n)
      setMaxError(n < assignedRows.length ? 'Cannot be less than current assigned count.' : '')
    }
  }

  const handleMaxApply = async () => {
    if (!org || maxValue === '' || maxSaving || maxError) return
    setMaxSaving(true)
    try {
      const updated: Organization = await updateOrganization({ ...(org as any), zelloUserMax: maxValue } as any)
      const saved = { ...(org as any), ...(updated as any), zelloUserMax: maxValue } as Organization
      if (setSettingsOrg) setSettingsOrg(saved)
      // Keep the active org current so the Users page's Add/Edit User license
      // checks see the new seat count without a refresh.
      if (selectedOrg?.id === saved.id) setSelectedOrg(saved)
    } catch (err: any) {
      setMaxError(err?.message || 'Unable to save.')
    } finally {
      setMaxSaving(false)
    }
  }

  const handleAdd = async () => {
    if (!org || !selectedOrgId || !addUserId) return
    setAddLoading(true)
    setAddError('')
    try {
      await createLicense(selectedOrgId, addUserId)
      await reloadLicenses()
      setAddUserId('')
    } catch (err: any) {
      setAddError(err?.message || 'Unable to add user.')
    } finally {
      setAddLoading(false)
    }
  }

  const handleDelete = async (licenseId: string) => {
    if (!selectedOrgId || deletingId) return
    setDeletingId(licenseId)
    try {
      await deleteLicense(licenseId, selectedOrgId)
      await reloadLicenses()
    } finally {
      setDeletingId(null)
    }
  }

  const tooltipText =
    'This sets the amount of voice user emails allowed to talk from the portal or on mobile devices. A specific user (ex: JohnDoe@gmail.com) can talk on one device at a time. Subsequent active logins by the same user will disconnect the original login from the voice channel.'

  const assignedCount = assignedRows.length
  const slotLabel = maxValue !== '' ? `${assignedCount} / ${maxValue}` : `${assignedCount}`

  return (
    <Wrapper>
      <div style={{ color: PRIMARY_COLOR, fontFamily: 'Montserrat', fontSize: '1.8rem', fontWeight: 'bold', lineHeight: '2.9rem', textAlign: 'left' }}>
        Voice Settings
      </div>
      {org && (
        <>
          {/* ── two-column header ── */}
          <TwoCol>
            <Col>
              <ColLabel>
                Allowed voice users
                <Tooltip title={<span>{tooltipText}</span>} placement="right" arrow>
                  <InfoTrigger role="button" tabIndex={0} aria-label="Allowed voice users info">
                    <InfoOutlinedIcon fontSize="small" style={{ display: 'block' }} />
                  </InfoTrigger>
                </Tooltip>
              </ColLabel>
              {superAdmin ? (
                <NumberInput
                  disableUnderline
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={maxValue}
                  onChange={handleMaxChange}
                  placeholder="—"
                />
              ) : (
                <ValueBox style={{ marginTop: 8 }}>{maxValue !== '' ? maxValue : '—'}</ValueBox>
              )}
            </Col>

            <Col>
              <ColLabel>Assigned licenses</ColLabel>
              <div style={{ display: 'flex', alignItems: 'center', minHeight: 44 }}>
                {licensesLoading ? (
                  <span style={{ fontSize: 12, opacity: 0.7 }}>Loading…</span>
                ) : (
                  <LicensePill>{slotLabel}</LicensePill>
                )}
              </div>
            </Col>
          </TwoCol>

          {superAdmin && (
            <>
              {maxError && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{maxError}</div>}
              <ActionsRow>
                <ActionButton
                  variant="outlined"
                  onClick={() => { setMaxValue(originalMax); setMaxError('') }}
                  disabled={maxSaving || !isDirty}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  onClick={handleMaxApply}
                  disabled={maxSaving || maxValue === '' || !isDirty || !!maxError}
                >
                  {maxSaving ? 'Saving…' : 'Apply Changes'}
                </ActionButton>
              </ActionsRow>
            </>
          )}

          <TablesRow>
          {/* ── assigned users table ── */}
          <TableSection>
            <TableSectionHeader>Assigned voice users</TableSectionHeader>
            <Paper elevation={1} style={{ maxWidth: 400, maxHeight: 360, overflowY: 'auto' }}>
              {/* Fixed layout with a reserved action column: under auto layout a long
                  selected email widened the first column past the Paper, which clips
                  horizontally (overflowY auto), cutting off the Add button. */}
              <Table size="small" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: ACTION_COL_WIDTH }} />
                </colgroup>
                <TableBody>
                  {assignedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} style={{ ...cellStyle, color: '#888', fontStyle: 'italic' }}>
                        No assigned users
                      </TableCell>
                    </TableRow>
                  )}
                  {assignedRows.map(({ license, label }) => (
                    <TableRow key={license.id} hover>
                      <TableCell style={{ ...cellStyle, wordBreak: 'break-all' }}>{label}</TableCell>
                      <TableCell padding="none" align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(license.id)}
                          disabled={!!deletingId}
                          aria-label={`Remove ${label}`}
                        >
                          <DeleteIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow>
                    <TableCell style={{ ...cellStyle, borderTop: '1px solid rgba(0,0,0,0.08)', paddingRight: 4 }}>
                      <Select
                        value={addUserId}
                        onChange={e => { setAddUserId(e.target.value as string); setAddError('') }}
                        displayEmpty
                        disableUnderline
                        style={{ fontFamily: 'Montserrat', fontSize: 12, width: '100%', textTransform: 'none' }}
                      >
                        <MenuItem value="" disabled style={{ fontFamily: 'Montserrat', fontSize: 12, textTransform: 'none' }}>
                          <em style={{ textTransform: 'none', fontStyle: 'italic' }}>Select a user to add…</em>
                        </MenuItem>
                        {unassignedUsers.map(u => (
                          <MenuItem key={u.id} value={u.id} style={{ fontFamily: 'Montserrat', fontSize: 12, textTransform: 'none' }}>
                            {u.attributes?.email ?? u.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell padding="none" align="center" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                      <ActionButton
                        variant="outlined"
                        size="small"
                        onClick={handleAdd}
                        disabled={addLoading || !addUserId}
                        style={{ padding: '3px 10px', fontSize: '0.85rem', marginTop: 0, marginRight: 0 }}
                      >
                        {addLoading ? '…' : 'Add'}
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
            {(addError || licensesError) && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{addError || licensesError}</div>
            )}
          </TableSection>

          {essentialOnlyButtonsFlag && <EssentialOnlyButtonsSection org={org} readOnly dense />}
          </TablesRow>
        </>
      )}
    </Wrapper>
  )
}

export default VoiceSettings
